import React, { useState } from 'react';
import { Star } from 'lucide-react';

interface StarRatingInputProps {
  value: number;
  onChange: (rating: number) => void;
  max?: number;
  size?: 'md' | 'lg';
  id?: string;
}

export function StarRatingInput({
  value,
  onChange,
  max = 5,
  size = 'lg',
  id = 'review-rating',
}: StarRatingInputProps) {
  const [hover, setHover] = useState<number | null>(null);
  const display = hover ?? value;
  const iconSize = size === 'lg' ? 36 : 28;
  const labels = ['', 'Mycket dåligt', 'Dåligt', 'Okej', 'Bra', 'Utmärkt'];

  return (
    <div>
      <div
        id={id}
        role="radiogroup"
        aria-label="Betyg"
        className="flex flex-wrap items-center gap-1"
        onMouseLeave={() => setHover(null)}
      >
        {Array.from({ length: max }, (_, i) => {
          const star = i + 1;
          const active = star <= display;
          return (
            <button
              key={star}
              type="button"
              role="radio"
              aria-checked={value === star}
              aria-label={`${star} av ${max} stjärnor`}
              title={labels[star]}
              className="rounded-md p-1.5 transition-colors hover:bg-purple-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
              onMouseEnter={() => setHover(star)}
              onFocus={() => setHover(star)}
              onBlur={() => setHover(null)}
              onClick={() => onChange(star)}
            >
              <Star
                size={iconSize}
                className={active ? 'text-amber-400' : 'text-slate-400'}
                fill={active ? 'currentColor' : 'none'}
                strokeWidth={active ? 1.5 : 2}
                aria-hidden
              />
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-sm text-slate-400" aria-live="polite">
        {value > 0 ? (
          <>
            <span className="font-medium text-amber-400">{value} av {max}</span>
            {labels[value] ? ` — ${labels[value]}` : ''}
          </>
        ) : (
          <span className="text-slate-500">Välj betyg genom att klicka på stjärnorna</span>
        )}
      </p>
    </div>
  );
}
